import React, {
  useCallback,
  useEffect,
  useState,
  PropsWithChildren,
  ReactNode,
} from "react";
import Navigation from "../Navigation";
import useNavigation from "src/hooks/useNavigation";
import { useLocation } from "react-router-dom";
import { useAuth } from "../utils/auth/assist/authContext";
import insp, { install } from "inspectlet-es";
import LoginAs from "./loginAs";
import TOSModal from "./TOSModal";
import { TOS_OVERLAY } from "../../constants/zIndexes";
import {
  DEFAULT_GUTTER_WIDTH_DESKTOP,
  DEFAULT_GUTTER_WIDTH_MOBILE,
  DEFAULT_PAGE_BOTTOM_OFFSET_MOBILE,
  DEFAULT_PAGE_BOTTOM_OFFSET_DESKTOP,
  MOBILE_HEADER_HEIGHT,
  SIDEBAR_EXPANDED_WIDTH,
  SIDEBAR_COLLAPSED_WIDTH
} from "../../constants/ui";
import useIsMobile from "src/hooks/useIsMobile";
import { buildSigninUrl } from "../../utils/loginRedirect";

const PageLayout = ({
  children,
  pageBG = "",
  suppressNavigation = false,
  suppressGutters = false,
  suppressTopPadding = false,
  pinnedHeader,
  pinnedHeaderHeight = 0,
}: PropsWithChildren & {
  pageBG?: string;
  suppressNavigation?: boolean;
  suppressGutters?: boolean;
  suppressTopPadding?: boolean;
  pinnedHeader?: ReactNode;
  pinnedHeaderHeight?: number;
}) => {
  install(1808888316);

  const { isAuth, isAuthLoading, user, hasAcceptedTerms } = useAuth();

  const location = useLocation();
  const currentPage = location.pathname;
  const [isLoggedInAs, setIsLoggedInAs] = useState(false);
  const [showTos, setShowTos] = useState(false);
  const { isDesktopSidebarExpanded } = useNavigation();
  const isMobile = useIsMobile();

  const pageLayoutClassNames = [
    `
    page-layout-main \
    px-[${suppressGutters ? 0 : DEFAULT_GUTTER_WIDTH_MOBILE}px] \
    md:px-[${suppressGutters ? 0 : DEFAULT_GUTTER_WIDTH_DESKTOP}px] \
    pb-[${suppressGutters ? 0 : DEFAULT_PAGE_BOTTOM_OFFSET_MOBILE}px] \
    md:pb-[${suppressGutters ? 0 : DEFAULT_PAGE_BOTTOM_OFFSET_DESKTOP}px] \
    `,
  ];

  if (!suppressTopPadding) {
    pageLayoutClassNames.push("md:pt-[16px]");
  }

  if (!isDesktopSidebarExpanded && !suppressNavigation) {
    pageLayoutClassNames.push(
      `overflow-visible md:ml-[100px] print:md:ml-0 pageLayout ${pageBG} bg-cover hideScrollBar`
    );
  } else if (isDesktopSidebarExpanded && !suppressNavigation) {
    pageLayoutClassNames.push(
      `overflow-visible md:ml-[240px] print:ml-0 pageLayout ${pageBG} bg-cover hideScrollBar`
    );
  } else {
    pageLayoutClassNames.push(
      `overflow-visible ml-[0px] authPageLayout ${pageBG} bg-cover hideScrollBar`
    );
  }

  const handleTOSAccepted = useCallback(() => {
    setShowTos(false);
  }, []);

  const navigate = () => {
    if (
      currentPage !== "/signin" &&
      currentPage !== "/forgotpassword" &&
      !location.pathname.startsWith("/forgotpassword/") &&
      !isAuth
    ) {
      const redirectLink = document.createElement("a");
      redirectLink.href = buildSigninUrl(window.location);
      document.body.appendChild(redirectLink);
      if (!isAuth) {
        redirectLink.click();
      }
    }
  };

  useEffect(() => {
    insp(["identify", user?.email]);
    // window.__insp.push(['identify', "john@example.com"]);
  }, [user?.email]);

  useEffect(() => {
    // Redirect to signin if not authenticated and not loading
    if (!isAuthLoading && !isAuth) {
      navigate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuth, isAuthLoading, currentPage]);

  useEffect(() => {
    setShowTos(isAuth && !isLoggedInAs && !hasAcceptedTerms);
  }, [hasAcceptedTerms, isLoggedInAs, isAuth]);

  return (
    <div className="page-layout hideScrollBar">
      {showTos && (
        <div
          className="bg-hero-pattern h-screen w-screen bg-cover fixed overflow-y-scroll "
          style={{
            zIndex: TOS_OVERLAY,
          }}
        >
          <TOSModal
            onAccepted={handleTOSAccepted}
            open={true}
            title={"Landvoice License And Service Agreement"}
          ></TOSModal>
        </div>
      )}

      <LoginAs setLoginAs={setIsLoggedInAs} />
      {currentPage === "/signin" ||
      currentPage === "/forgotpassword" ||
      location.pathname.startsWith("/forgotpassword/") ||
      (isAuth && !showTos) ? (
        <main className={pageLayoutClassNames.join(" ")}>
          {suppressNavigation ? "" : <Navigation />}
          <div
            className="page-layout-content mt-[60px] md:mt-0"
          >
            {pinnedHeader && (
              <div
                className="page-header-container fixed top-0 left-0 right-0 z-50 bg-white"
                style={{
                  ...(isMobile && { left: 0, top: `${MOBILE_HEADER_HEIGHT}px` }),
                  ...(!isMobile && { left: isDesktopSidebarExpanded ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH, top: 0 }),
                }}
              >
                {pinnedHeader}
              </div>
            )}
            <div
              style={pinnedHeader ? {
                ...(!isMobile && {
                  marginTop: pinnedHeaderHeight,
                }),
                ...(isMobile && { marginTop: `${(MOBILE_HEADER_HEIGHT + pinnedHeaderHeight)}px` }),
              } : {}}
            >
              {children}
            </div>
          </div>
        </main>
      ) : null}
    </div>
  );
};

export default PageLayout;
